import React from "react";
import { PushCategory } from "@scrile/api-provider/dist/api/PushNotificationsProvider";
import { useTranslation } from "locales";
import BaseCard from "components/BaseCard";
import BaseSpinner from "components/BaseSpinner";
import CheckboxInput from "components/CheckboxInput";
import useWebPush from "hooks/useWebPush";
import useController from "./controller";
import "./style.scss";

const CATEGORY_LABELS: Record<PushCategory, string> = {
  [PushCategory.MESSAGES]: "Messages",
  [PushCategory.CALLS]: "Calls",
  [PushCategory.LIVECHATS]: "Live streams",
  [PushCategory.POSTS]: "Posts",
  [PushCategory.REVIEWS]: "Reviews",
};

export default function PageNotificationSettings() {
  const { t } = useTranslation();
  const { categories, settings, loading, toggleCategory } = useController();
  const { isSupported, permission, subscribe } = useWebPush();

  if (loading) {
    return (
      <BaseCard>
        <div className="notification-settings notification-settings__loading">
          <BaseSpinner size={32} />
        </div>
      </BaseCard>
    );
  }

  const permissionDenied = isSupported && permission === "denied";
  const notSupported = !isSupported;

  return (
    <BaseCard>
      <div className="notification-settings">
        <h2 className="headline-2 mb-5">{t("Push notifications")}</h2>

        {notSupported && (
          <div className="notification-settings__warning body mb-5">
            {t("Push notifications are not supported in this browser.")}
          </div>
        )}

        {permissionDenied && (
          <div className="notification-settings__warning body mb-5">
            {t("Push notifications are blocked. Please enable them in your browser settings.")}
          </div>
        )}

        {permission === "default" && isSupported && (
          <div className="notification-settings__prompt mb-5">
            <p className="body mb-3">{t("Enable push notifications to receive updates when you're away.")}</p>
            <button className="base-button __large" onClick={subscribe}>
              {t("Enable notifications")}
            </button>
          </div>
        )}

        <div className="notification-settings__categories">
          {categories.map((category) => (
            <div key={category} className="notification-settings__row">
              <CheckboxInput
                name={category}
                value={!!settings[category]}
                onChange={() => toggleCategory(category)}
              >
                <span className="notification-settings__label">{t(CATEGORY_LABELS[category])}</span>
              </CheckboxInput>
            </div>
          ))}
        </div>

        <p className="notification-settings__hint body mt-5">
          {t("Choose which types of notifications you want to receive.")}
        </p>
      </div>
    </BaseCard>
  );
}
